/* Copilot spotlight — scrolls a [data-tour] anchor into view and flashes a
 * dimmed backdrop + amber ring around it (the walkthrough system's visual
 * language, minus the tooltip). Self-removing; safe to call right after a
 * navigation — it retries while the lazy-loaded page mounts. */

const MAX_TRIES = 40; // × 100ms ≈ 4s for lazy routes/tabs to mount
const RING_PAD = 8;
const RING_LIFETIME_MS = 2600;

// Only one spotlight at a time — a new call cancels the previous retry loop and
// ring so stacked navigate+highlight turns don't leave orphans on stale pages.
let activeCancel = null;

function prefersReducedMotion() {
  return (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );
}

function flashRect(el, onCleanup) {
  const ring = document.createElement("div");
  ring.className = "fk-spotlight-ring";
  const place = () => {
    const r = el.getBoundingClientRect();
    ring.style.top = `${r.top - RING_PAD}px`;
    ring.style.left = `${r.left - RING_PAD}px`;
    ring.style.width = `${r.width + RING_PAD * 2}px`;
    ring.style.height = `${r.height + RING_PAD * 2}px`;
  };
  place();
  document.body.appendChild(ring);
  // Follow the element while it (or the page) scrolls, rather than vanishing on
  // the first scroll event — the fix for far-away targets after navigation.
  let raf = 0;
  const onScroll = () => {
    if (raf) return;
    raf = requestAnimationFrame(() => {
      raf = 0;
      place();
    });
  };
  window.addEventListener("scroll", onScroll, { capture: true, passive: true });
  window.addEventListener("resize", onScroll, { passive: true });
  const cleanup = () => {
    window.removeEventListener("scroll", onScroll, { capture: true });
    window.removeEventListener("resize", onScroll);
    if (raf) cancelAnimationFrame(raf);
    ring.remove();
  };
  const timer = setTimeout(cleanup, RING_LIFETIME_MS);
  onCleanup(() => {
    clearTimeout(timer);
    cleanup();
  });
}

/* Set a controlled React <input>/<textarea> value the way a real keystroke
 * would: the native value setter + a bubbling 'input' event, so React's
 * onChange fires and any debounced search runs. */
export function setReactValue(el, value) {
  const proto =
    el.tagName === "TEXTAREA"
      ? window.HTMLTextAreaElement.prototype
      : window.HTMLInputElement.prototype;
  const setter = Object.getOwnPropertyDescriptor(proto, "value")?.set;
  if (setter) setter.call(el, value);
  else el.value = value;
  el.dispatchEvent(new Event("input", { bubbles: true }));
}

// Is `el` a visible, usable text/search input we can type into?
function isVisibleInput(el) {
  if (!el || (el.tagName !== "INPUT" && el.tagName !== "TEXTAREA")) return false;
  const t = (el.getAttribute("type") || "text").toLowerCase();
  if (["hidden", "checkbox", "radio", "range", "file", "submit", "button"].includes(t))
    return false;
  if (el.disabled || el.readOnly) return false;
  if (el.closest("[data-copilot-root]")) return false; // never the copilot's own input
  const r = el.getBoundingClientRect();
  return r.width > 1 && r.height > 1 && el.offsetParent !== null;
}

// Find the search/text input to fill when a data-tour anchor isn't present —
// so the copilot can fill the search box on ANY page (admin lists, etc.), not
// only the quote pages that carry data-tour anchors.
function findGenericSearchInput() {
  const inputs = Array.from(document.querySelectorAll("input, textarea")).filter(
    isVisibleInput
  );
  const searchish = inputs.find((el) => {
    const t = (el.getAttribute("type") || "").toLowerCase();
    const ph = (el.getAttribute("placeholder") || "").toLowerCase();
    const aria = (el.getAttribute("aria-label") || "").toLowerCase();
    return t === "search" || /search|filter|find/.test(ph + " " + aria);
  });
  return searchish || inputs[0] || null;
}

/**
 * Type a value into the input at a [data-tour] anchor (or, if none, the page's
 * visible search input), then spotlight it. Retries while a just-navigated-to
 * page mounts. Returns {ok,label} AFTER verifying the value actually landed —
 * so the caller never reports a false success.
 * @param {string} target - anchor name (e.g. "eq-search-bar") or "" for generic
 * @param {string} value
 * @param {number} _tries
 * @returns {{ok:boolean,label:string}}
 */
export function fillTarget(target, value, _tries = 0) {
  const v = String(value ?? "");
  const host = target
    ? document.querySelector(`[data-tour="${CSS.escape(target)}"]`)
    : null;
  let input =
    host && (host.matches("input, textarea") ? host : host.querySelector("input, textarea"));
  // Anchor absent (or this page has no anchor) → fall back to the visible search box.
  if (!input || !isVisibleInput(input)) input = findGenericSearchInput();
  if (!input) {
    if (_tries < MAX_TRIES) {
      setTimeout(() => fillTarget(target, value, _tries + 1), 100);
      return { ok: true, label: `typing “${v}”…` }; // provisional; retry in flight
    }
    return { ok: false, label: "couldn't find a field to type into on this screen" };
  }
  input.focus();
  setReactValue(input, v);
  if (target) spotlight(target);
  // Verify-after-act: React-controlled inputs can reject a programmatic set.
  const landed = String(input.value ?? "");
  if (landed !== v && !(v && landed.includes(v))) {
    return { ok: false, label: `couldn't type into that field` };
  }
  return { ok: true, label: `typed “${v}”` };
}

// Resolve a copilot control ref (c1, c2…) to its live DOM element. scanActionable
// stamps [data-copilot-ref="cN"] on every scanned control, so we can find it here
// without importing from copilotControls (which would be a circular dependency).
function elForRef(ref) {
  if (!ref) return null;
  try {
    const el = document.querySelector(
      `[data-copilot-ref="${CSS.escape(String(ref))}"]`
    );
    if (el && el.isConnected) {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) return el;
    }
  } catch {
    /* bad ref */
  }
  return null;
}

/**
 * Point at a scanned control by its ref (c1, c2…): scroll it to center and flash
 * the amber ring around it. Honest — returns {ok,label}; ok:false when the ref
 * no longer resolves (the DOM changed since the scan) so the caller can chip ✗.
 * @param {string} ref - a copilot control ref, e.g. "c7"
 * @returns {{ok:boolean,label:string}}
 */
export function spotlightRef(ref) {
  if (activeCancel) activeCancel();
  const el = elForRef(ref);
  if (!el) return { ok: false, label: "that control isn't on screen anymore" };
  const reduce = prefersReducedMotion();
  try {
    el.scrollIntoView({
      behavior: reduce ? "auto" : "smooth",
      block: "center",
    });
  } catch {
    /* jsdom */
  }
  let ringCleanup = null;
  activeCancel = () => {
    if (ringCleanup) ringCleanup();
    activeCancel = null;
  };
  // Place after the smooth scroll settles; the ring then tracks the element.
  setTimeout(
    () => flashRect(el, (fn) => (ringCleanup = fn)),
    reduce ? 30 : 420
  );
  const label =
    (el.getAttribute("aria-label") ||
      el.getAttribute("title") ||
      (el.innerText || el.textContent || "").replace(/\s+/g, " ").trim() ||
      "that control").slice(0, 48);
  return { ok: true, label: `Pointing at ${label}` };
}

/**
 * @param {string} target - a data-tour anchor name, e.g. "eq-search-bar"
 */
export default function spotlight(target) {
  if (!target) return;
  if (activeCancel) activeCancel();
  let cancelled = false;
  let ringCleanup = null;
  let retryTimer = null;
  activeCancel = () => {
    cancelled = true;
    if (retryTimer) clearTimeout(retryTimer);
    if (ringCleanup) ringCleanup();
    activeCancel = null;
  };

  let tries = 0;
  const attempt = () => {
    if (cancelled) return;
    const el = document.querySelector(`[data-tour="${CSS.escape(target)}"]`);
    const visible = el && el.getBoundingClientRect().width > 0;
    if (!visible) {
      tries += 1;
      if (tries < MAX_TRIES) retryTimer = setTimeout(attempt, 100);
      return;
    }
    const reduce = prefersReducedMotion();
    el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "center" });
    // Place after the smooth scroll settles; the ring then tracks the element,
    // so an imperfect delay no longer misplaces or kills it.
    retryTimer = setTimeout(
      () => {
        if (!cancelled) flashRect(el, (fn) => (ringCleanup = fn));
      },
      reduce ? 30 : 420
    );
  };
  attempt();
}
